/**
 * What a call did, read from its tool log (voice_call_events) in words a finance person scans: a promise with its date and
 * method, documents sent, a dispute logged. Pure, so the list chips and the drawer timeline read the same lines.
 * Arguments came from the model during the call: they are shown as text, never acted on here.
 */
export interface CallToolEvent {
  /** The tool call's own id, unique within its call: the tie-break when two actions share a timestamp. */
  id: string; tool: string; arguments: Record<string, unknown>; ok: boolean; at: string;
  /** The call's currency (from its brief); a promise's amount is in it. */
  currency?: string | null;
}
export interface CallAction {
  id: string; at: string; tool: string; ok: boolean; text: string;
  /** promise and claim carry money; person means a human has to act; done is what the agent already did. */
  tone: 'promise' | 'claim' | 'person' | 'done';
}

const methods: Record<string, string> = { ach: 'bank transfer (ACH)', wire: 'wire', check: 'check', card: 'card', portal: 'their AP portal' };
const disputes: Record<string, string> = {
  wrong_amount: 'wrong amount', not_received: 'not received', already_paid: 'already paid', service_issue: 'service issue',
  missing_po: 'missing PO', duplicate: 'duplicate', other: 'other',
};
const documents: Record<string, string> = {
  invoice_copies: 'Sent invoice copies', statement: 'Sent the account statement', payment_instructions: 'Sent payment instructions',
};
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const day = (iso: string) => {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
};
const money = (n: unknown, currency: string | null | undefined) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n); }
  catch { return null; }
};
const covering = (numbers: string[]) => (numbers.length ? ` for ${numbers.join(', ')}` : '');

function describe(e: CallToolEvent): Omit<CallAction, 'id' | 'at' | 'tool' | 'ok'> | null {
  const a = e.arguments;
  switch (e.tool) {
    case 'record_promise_to_pay': {
      const method = methods[str(a.method)];
      return { tone: 'promise', text: `Promised ${money(a.amount, e.currency) ?? 'the overdue balance'} by ${day(str(a.pay_date))}${method ? `, by ${method}` : ''}` };
    }
    case 'report_payment_made': {
      const method = methods[str(a.method)];
      return { tone: 'claim', text: `Says paid ${day(str(a.paid_date))}${method ? ` by ${method}` : ''}${covering(list(a.invoice_numbers))}` };
    }
    case 'log_dispute': return { tone: 'person', text: `Dispute: ${disputes[str(a.reason)] ?? 'other'}${covering(list(a.invoice_numbers))}` };
    case 'send_documents': return { tone: 'done', text: `${documents[str(a.what)] ?? 'Sent documents'}${covering(list(a.invoice_numbers))}` };
    case 'request_callback': return { tone: 'done', text: `Callback set: ${str(a.when).slice(0, 60) || 'time agreed'}` };
    case 'report_wrong_contact': return { tone: 'person', text: 'Wrong contact' };
    case 'mark_do_not_call': return { tone: 'person', text: 'Asked not to be called again' };
    case 'escalate_to_human': return { tone: 'person', text: 'Asked for a person' };
    default: return null;
  }
}

/** Every action the call took, oldest first. Lookups are reading, not acting, so they are left out. */
export function callActions(events: CallToolEvent[]): CallAction[] {
  return [...events].sort((x, y) => Date.parse(x.at) - Date.parse(y.at) || x.id.localeCompare(y.id)).flatMap(e => {
    const d = e.tool.startsWith('lookup_') ? null : describe(e);
    return d ? [{ ...d, id: e.id, at: e.at, tool: e.tool, ok: e.ok }] : [];
  });
}

/** The chips on a list row: what worked, the money first, one chip per kind of action. */
export function callChips(events: CallToolEvent[]): CallAction[] {
  const order = { promise: 0, claim: 1, person: 2, done: 3 } as const;
  const seen = new Set<string>();
  return callActions(events).filter(a => a.ok).reverse().filter(a => (seen.has(a.tool) ? false : (seen.add(a.tool), true)))
    .sort((x, y) => order[x.tone] - order[y.tone]);
}
